/* ============================================================================
   GraceYes — theme.js

   Only what the design genuinely needs. Vanilla, no dependencies, no
   polyfills, no build step.

     1. Motion      the reveal and stagger observers behind html.gy-motion,
                    with the passive sweep and the safety net the live site
                    ships alongside them
     2. Drawers     one controller for the cart drawer, the filter drawer and
                    the mobile navigation: open, close, Escape, focus trap,
                    body scroll lock
     3. Menu pages  the drawer's horizontal card stack (drill into Shop, slide
                    the whole row left by one panel)
     4. Accordion   exclusive groups, and every <details> opens for printing
     5. Slider      the gallery: prev / next / dots / thumbnails / keyboard
     6. Quantity    the stepper, with a live total hook
     7. Tabs        a real ARIA tablist, arrow keys and all

   Everything is opt-in through data attributes, so a section that does not
   use a behaviour costs nothing. Nothing here fakes a WordPress feature: the
   concern filter, the ingredient glossary, the wishlist and the WhatsApp
   message composition are all left to their own placeholders.
   ========================================================================= */

(function () {
  'use strict';

  var doc = document;
  var motionOK = doc.documentElement.classList.contains('gy-motion');

  function $(sel, root) { return (root || doc).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }
  function on(el, type, fn, opts) { if (el) el.addEventListener(type, fn, opts || false); }

  /* --------------------------------------------------------------------------
     Idempotence.

     init() runs again on every shopify:section:load, which the theme editor
     fires on each save. Without these two guards a single element collects a
     fresh listener per reload: one tap on a menu chevron would push the panel
     index onto the stack twice and Back would need two presses, quantity
     steppers would jump two at a time, and the window scroll sweep would run
     N times a frame.

       claim(el, key)  true the first time this element is wired for `key`
       once(key)       true the first time, for document- and window-level
                       listeners that must exist exactly once per page
     ------------------------------------------------------------------------ */
  var claimed = new WeakMap();
  function claim(el, key) {
    if (!el) return false;
    var keys = claimed.get(el);
    if (!keys) { keys = {}; claimed.set(el, keys); }
    if (keys[key]) return false;
    keys[key] = true;
    return true;
  }

  var onceKeys = {};
  function once(key) {
    if (onceKeys[key]) return false;
    onceKeys[key] = true;
    return true;
  }

  /* -------------------------------------------------------------------------
     1. MOTION

     Two observers. Both add .is-in; theme.css owns what that means. Because
     every hidden state is scoped under html.gy-motion, none of this is load
     bearing — if the observers never run the content is simply there.
     ---------------------------------------------------------------------- */

  // The sweep: anything already above the fold, revealed without waiting for
  // a scroll event that may never come. Module scope, so the window listeners
  // registered once below and the per-init call share one function.
  function sweep() {
    $$('.gy-rv:not(.is-in), .gy-st:not(.is-in)').forEach(function (el) {
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.94 && box.bottom > 0) el.classList.add('is-in');
    });
  }

  var revealer = null;
  var stagger = null;

  function initMotion() {
    if (!motionOK || !('IntersectionObserver' in window)) { revealAll(); return; }

    // Scroll-reveal on the storefront sections (the WordPress build's slight
    // rise-in on scroll). Headings reveal, cards and tiles stagger. Anything
    // already carrying a class keeps it; the tagging is additive only.
    $$('.gy-section-head, .gy-featured__head, .gy-journal__head, .gy-films__head, .gy-catgrid__head, .gy-yn__head, .gy-news__head, .gy-split__copy, .gy-pdp h3.sec-h, .gy-pdp .gy-formula .sec-head, .gy-coll__head')
      .forEach(function (el) { if (!el.classList.contains('gy-rv')) el.classList.add('gy-rv'); });
    $$('.gy-catgrid__cell, .gy-film, .gy-journal-card, .gy-yn__grid > *, .gy-split__media, .gy-pdp .benefit, .gy-pdp .step, .gy-pdp .ff, .gy-pdp details.faq, .gy-pdp .gy-glance, .gy-promises__list > li, .gy-stats > li')
      .forEach(function (el) { if (!el.classList.contains('gy-st')) el.classList.add('gy-st'); });

    var titles = $$('.gy-rv');
    var staggered = $$('.gy-st');

    // A clipped element reports a zero-height intersection in Chrome, so the
    // threshold for the reveal observer has to be 0, not 0.01.
    if (!revealer) revealer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    titles.forEach(function (el) { if (claim(el, 'rv')) revealer.observe(el); });

    // The delay is capped at seven steps so a twelve-card grid never leaves a
    // card waiting 720ms for its turn.
    var groups = new Map();
    staggered.forEach(function (el) {
      var parent = el.parentElement;
      var list = groups.get(parent);
      if (!list) { list = []; groups.set(parent, list); }
      el.style.setProperty('--gy-d', Math.min(list.length, 7) * 60 + 'ms');
      list.push(el);
    });

    if (!stagger) stagger = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-in');
        stagger.unobserve(el);
        // Release will-change once the transition has had time to finish.
        window.setTimeout(function () { el.classList.add('gy-st--done'); }, 1400);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    staggered.forEach(function (el) { if (claim(el, 'st')) stagger.observe(el); });

    sweep();

    if (once('motion-globals')) {
      on(window, 'scroll', sweep, { passive: true });
      on(window, 'resize', sweep, { passive: true });

      // The net. Six seconds in, reveal whatever is on screen but has not
      // intersected (a backgrounded tab, a clipped layout) — not the whole
      // page, or every section below the fold would lose its rise-in. The
      // observers keep running for the rest. Only a missing observer API
      // reveals everything (handled above).
      window.setTimeout(sweep, 6000);
      document.addEventListener('visibilitychange', function () { if (!document.hidden) sweep(); });
    }
  }

  function revealAll() {
    $$('.gy-rv, .gy-st').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* -------------------------------------------------------------------------
     2. DRAWERS — cart, filters, mobile navigation

     Markup contract:
       <button data-gy-drawer-open="cart-drawer">
       <div class="gy-drawer" id="cart-drawer" role="dialog" aria-modal="true"
            aria-label="Cart" hidden>
         <div class="gy-drawer__scrim" data-gy-drawer-close></div>
         <div class="gy-drawer__panel"> ... </div>
       </div>
     ---------------------------------------------------------------------- */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
                  ' select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  var openDrawerEl = null;
  var lastTrigger = null;

  function openDrawer(el, trigger) {
    if (!el || el === openDrawerEl) return;
    if (openDrawerEl) closeDrawer();
    openDrawerEl = el;
    lastTrigger = trigger || null;
    el.hidden = false;
    // A frame between removing [hidden] and adding .is-open, so the panel has
    // a state to transition from.
    window.requestAnimationFrame(function () { el.classList.add('is-open'); });
    doc.body.classList.add('gy-no-scroll');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var first = $(FOCUSABLE, el);
    if (first) first.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('gy:drawer:open', { bubbles: true }));
  }

  function closeDrawer() {
    var el = openDrawerEl;
    if (!el) return;
    openDrawerEl = null;
    el.classList.remove('is-open');
    doc.body.classList.remove('gy-no-scroll');
    $$('[data-gy-drawer-open="' + el.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    var restore = lastTrigger;
    lastTrigger = null;
    // Wait for the slide out before hiding, unless motion is off.
    var delay = motionOK ? 420 : 0;
    window.setTimeout(function () { if (!el.classList.contains('is-open')) el.hidden = true; }, delay);
    if (restore && doc.contains(restore)) restore.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('gy:drawer:close', { bubbles: true }));
  }

  function initDrawers() {
    // Both listeners are delegated from the document, so they cover sections
    // that do not exist yet and must be registered exactly once.
    if (!once('drawers')) return;

    on(doc, 'click', function (ev) {
      var opener = ev.target.closest('[data-gy-drawer-open]');
      if (opener) {
        // The attribute must name a drawer by id. A bare, valueless
        // data-gy-drawer-open is somebody else's flag — sections/header.liquid
        // uses it that way for the burger and drives its own drawer — so this
        // controller deliberately ignores it rather than fighting over the
        // same click.
        var id = opener.getAttribute('data-gy-drawer-open');
        var target = id ? doc.getElementById(id) : null;
        if (target) { ev.preventDefault(); openDrawer(target, opener); }
        return;
      }
      if (ev.target.closest('[data-gy-drawer-close]')) { ev.preventDefault(); closeDrawer(); }
    });

    on(doc, 'keydown', function (ev) {
      // Same courtesy on Escape: if this controller has nothing open, some
      // other controller does, and it can handle its own key.
      if (!openDrawerEl) return;
      if (ev.key === 'Escape') { ev.preventDefault(); closeDrawer(); return; }
      if (ev.key !== 'Tab') return;
      // Focus trap. The drawer is the whole dialog, so the cycle is the
      // dialog's own focusables in DOM order.
      var items = $$(FOCUSABLE, openDrawerEl).filter(function (el) {
        return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (ev.shiftKey && doc.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && doc.activeElement === last) { ev.preventDefault(); first.focus(); }
    });
  }

  /* -------------------------------------------------------------------------
     3. MENU PAGES — the drawer's horizontal card stack

     Every level of the navigation is a full-width panel laid out in one flex
     row; drilling in translates the row left by one panel width. It is the
     most characterful piece of motion in the chrome, and it is four lines.

       <div data-gy-menu>
         <button data-gy-menu-back hidden>Back</button>
         <div data-gy-menu-track>
           <div data-gy-menu-panel> ... <button data-gy-menu-to="1"> ... </div>
           <div data-gy-menu-panel data-gy-menu-index="1"> ... </div>
     ---------------------------------------------------------------------- */

  function initMenuPages() {
    $$('[data-gy-menu]').forEach(function (menu) {
      if (!claim(menu, 'menu')) return;
      var track = $('[data-gy-menu-track]', menu);
      var back = $('[data-gy-menu-back]', menu);
      if (!track) return;
      var stack = [0];

      function go(index) {
        track.style.transform = 'translateX(calc(' + index + ' * max(-100vw, -1 * var(--gy-drawer-w))))';
        $$('[data-gy-menu-panel]', track).forEach(function (panel, i) {
          // Panels off to one side must leave the tab order, or a keyboard
          // user walks into a menu they cannot see.
          panel.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          panel.inert = i !== index;
        });
        if (back) back.hidden = index === 0;
      }

      on(menu, 'click', function (ev) {
        var to = ev.target.closest('[data-gy-menu-to]');
        if (to) {
          ev.preventDefault();
          var index = parseInt(to.getAttribute('data-gy-menu-to'), 10) || 0;
          stack.push(index);
          go(index);
          var panel = $('[data-gy-menu-panel][data-gy-menu-index="' + index + '"]', track);
          var firstLink = panel && $(FOCUSABLE, panel);
          if (firstLink) firstLink.focus({ preventScroll: true });
          return;
        }
        if (ev.target.closest('[data-gy-menu-back]')) {
          ev.preventDefault();
          stack.pop();
          go(stack[stack.length - 1] || 0);
        }
      });

      go(0);
    });
  }

  /* -------------------------------------------------------------------------
     4. ACCORDION

     <details> already works on its own. Two things it does not do: behave as
     an exclusive group, and open itself for printing.
     ---------------------------------------------------------------------- */

  function initAccordions() {
    $$('[data-gy-accordion-exclusive]').forEach(function (group) {
      if (!claim(group, 'acc')) return;
      on(group, 'toggle', function (ev) {
        var opened = ev.target;
        if (!opened.open || opened.tagName !== 'DETAILS') return;
        $$('details', group).forEach(function (d) { if (d !== opened) d.open = false; });
      }, true);
    });

    // Printing: open everything, remember what was shut, put it back after.
    if (!once('print')) return;
    var reopened = [];
    on(window, 'beforeprint', function () {
      reopened = $$('details:not([open])');
      reopened.forEach(function (d) { d.open = true; });
    });
    on(window, 'afterprint', function () {
      reopened.forEach(function (d) { d.open = false; });
      reopened = [];
    });
  }

  /* -------------------------------------------------------------------------
     5. SLIDER — the product gallery, and any other one-at-a-time strip

       <div data-gy-slider>
         <div data-gy-slider-track>
           <div data-gy-slide> ... </div>
         </div>
         <button data-gy-slider-prev>  <button data-gy-slider-next>
         <div data-gy-slider-dots></div>            (dots are generated)
         <button data-gy-slider-thumb="0">          (or supply thumbnails)
     ---------------------------------------------------------------------- */

  function initSliders() {
    $$('[data-gy-slider]').forEach(function (root) {
      if (!claim(root, 'slider')) return;
      var slides = $$('[data-gy-slide]', root);
      if (slides.length < 2) {
        // One image is not a slider. Hide the controls rather than leaving
        // dead arrows on the page.
        $$('[data-gy-slider-prev], [data-gy-slider-next], [data-gy-slider-dots]', root)
          .forEach(function (el) { el.hidden = true; });
        return;
      }

      var index = 0;
      var dotsHost = $('[data-gy-slider-dots]', root);
      var thumbs = $$('[data-gy-slider-thumb]', root);
      var dots = [];

      if (dotsHost && !dotsHost.children.length) {
        slides.forEach(function (_, i) {
          var dot = doc.createElement('button');
          dot.type = 'button';
          dot.className = 'gy-slider__dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          on(dot, 'click', function () { show(i, true); });
          dotsHost.appendChild(dot);
          dots.push(dot);
        });
      }

      thumbs.forEach(function (thumb) {
        on(thumb, 'click', function () {
          show(parseInt(thumb.getAttribute('data-gy-slider-thumb'), 10) || 0, true);
        });
      });

      function show(next, focusSlide) {
        // Arrows wrap, so there is never a disabled state to render.
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          var current = i === index;
          slide.classList.toggle('is-current', current);
          slide.setAttribute('aria-hidden', current ? 'false' : 'true');
          slide.inert = !current;
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-current', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle('is-current', i === index);
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        root.dispatchEvent(new CustomEvent('gy:slider:change', { bubbles: true, detail: { index: index } }));
        if (focusSlide) {
          var live = $('[data-gy-slider-status]', root);
          if (live) live.textContent = 'Image ' + (index + 1) + ' of ' + slides.length;
        }
      }

      on($('[data-gy-slider-prev]', root), 'click', function () { show(index - 1, true); });
      on($('[data-gy-slider-next]', root), 'click', function () { show(index + 1, true); });

      on(root, 'keydown', function (ev) {
        if (ev.key === 'ArrowLeft')  { ev.preventDefault(); show(index - 1, true); }
        if (ev.key === 'ArrowRight') { ev.preventDefault(); show(index + 1, true); }
      });

      show(0, false);
    });
  }

  /* -------------------------------------------------------------------------
     6. QUANTITY STEPPER
       <div class="gy-qty" data-gy-qty>
         <button data-gy-qty-step="-1">  <input class="gy-qty__input">
         <button data-gy-qty-step="1">
     ---------------------------------------------------------------------- */

  function initQuantity() {
    $$('[data-gy-qty]').forEach(function (root) {
      if (!claim(root, 'qty')) return;
      var input = $('input', root);
      if (!input) return;
      $$('[data-gy-qty-step]', root).forEach(function (btn) {
        on(btn, 'click', function () {
          var step = parseInt(btn.getAttribute('data-gy-qty-step'), 10) || 1;
          var min = parseInt(input.getAttribute('min'), 10);
          var max = parseInt(input.getAttribute('max'), 10);
          var next = (parseInt(input.value, 10) || 0) + step;
          if (!isNaN(min) && next < min) next = min;
          if (!isNaN(max) && next > max) next = max;
          if (isNaN(min) && next < 1) next = 1;
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* -------------------------------------------------------------------------
     7. TABS — a real ARIA tablist

       <div data-gy-tabs>
         <div role="tablist">
           <button role="tab" aria-selected="true" aria-controls="p1" id="t1">
         <div role="tabpanel" id="p1" aria-labelledby="t1">
     ---------------------------------------------------------------------- */

  function initTabs() {
    $$('[data-gy-tabs]').forEach(function (root) {
      if (!claim(root, 'tabs')) return;
      var tabs = $$('[role="tab"]', root);
      if (!tabs.length) return;

      function select(tab, moveFocus) {
        tabs.forEach(function (t) {
          var current = t === tab;
          t.setAttribute('aria-selected', current ? 'true' : 'false');
          t.tabIndex = current ? 0 : -1;
          var panel = doc.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !current;
        });
        if (moveFocus) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        on(tab, 'click', function () { select(tab, false); });
        on(tab, 'keydown', function (ev) {
          var next = null;
          if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          if (ev.key === 'ArrowLeft'  || ev.key === 'ArrowUp')   next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (ev.key === 'Home') next = tabs[0];
          if (ev.key === 'End')  next = tabs[tabs.length - 1];
          if (next) { ev.preventDefault(); select(next, true); }
        });
      });

      var initial = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0];
      select(initial || tabs[0], false);
    });
  }

  /* ---------------------------------------------------------------------- */

  function init() {
    initMotion();
    initDrawers();
    initMenuPages();
    initAccordions();
    initSliders();
    initQuantity();
    initTabs();
  }

  if (doc.readyState === 'loading') { on(doc, 'DOMContentLoaded', init); } else { init(); }

  // Sections added or reordered in the theme editor need their behaviours back.
  on(doc, 'shopify:section:load', init);

  window.GY = {
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    revealAll: revealAll,
    refresh: init,
    qs: $,
    qsa: $$
  };
})();
